import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import OptionsTradingFlow from "./OptionsTradingFlow";
import QuickTradeModal from "./QuickTradeModal";
import TrailStopModal from "./TrailStopModal";
import { hapticTap, hapticMedium, hapticSuccess } from "../utils/haptics";
import { soundCardSettle, soundTap, soundTick, isSoundMuted, setSoundMuted } from "../utils/sound";
import LastLoginBadge from "./LastLoginBadge";
import AutomationHealthPill from "./AutomationHealthPill";
import OmegaTeaPage from "./OmegaTeaPage";
import { shareStat } from "../utils/shareStat";
// iter 96 — ExecutionConfirmModal extracted to /modals, re-exported here
// so existing imports keep working.
import { ExecutionConfirmModal } from "./modals/ExecutionConfirmModal";
import { AiExitVerdictModal } from "./modals/AiExitVerdictModal";
import { ActiveOrdersStrip } from "./modals/ActiveOrdersStrip";
import { SwingPlaysExpanded } from "./modals/SwingPlaysExpanded";
import { LossRecoveryModal } from "./modals/LossRecoveryModal";
import { ZiChatExpanded } from "./modals/ZiChatExpanded";
import ErrorBoundary from "./ErrorBoundary";
// iter 104 — Large expanded components extracted into /modals/* to keep
// SwipeCards.js focused on the home-grid shell + shared state.
import { AccountExpanded } from "./modals/AccountExpanded";
import { AutopilotExpanded } from "./modals/AutopilotExpanded";
import { PositionsExpanded } from "./modals/PositionsExpanded";
import { IntelExpanded } from "./modals/IntelExpanded";
import { MobileSettingsExpanded } from "./modals/MobileSettingsExpanded";
import { EliteExpanded } from "./modals/EliteExpanded";
import { CryptoExpanded } from "./modals/CryptoExpanded";
import OmegaBrainStatusCard from "./OmegaBrainStatusCard";
import OmegaTea from "./OmegaTea";
// iter 104 — shared helpers (formatters, regime colors, LifeCardPopup, API)
// now live in /modals/_shared.js
import {
  API, f$, fN,
  _regimeColorTile, _regimeDot,
} from "./modals/_shared";
import { fastGetAsync } from "../lib/fastFetch";
export { ExecutionConfirmModal, AiExitVerdictModal };

// ═══ CARD 1 EXPANDED: Account Overview ═══════════════════════════════════════
// Helper: show a pop-up "life card" with rich context for any tapped metric.

// iter 101 — regime dot + color helpers for home-grid badges.


// Compact re-usable skeleton block


// ═══ CARD 5 EXPANDED: Autopilot ═══════════════════════════════════════════════

// ═══ CARD 2 EXPANDED: Open Positions ═════════════════════════════════════════

// ═══ CARD 4 EXPANDED: Intelligence Feed ══════════════════════════════════════
// ═══ RECENTLY DISMISSED — "Why did I miss this?" panel =======================

// ═══ NOTIFICATIONS PERMISSION CARD ═══════════════════════════════════════════

// ═══ PREVIEW CARD COMPONENT ══════════════════════════════════════════════════
function Card({ gradient, icon, label, value, sub, extra, onClick }) {
  return (
    <div onClick={onClick} style={{
      minWidth: 150, flex: "1 1 150px", borderRadius: 20, padding: "18px 16px",
      background: gradient, cursor: "pointer",
      display: "flex", flexDirection: "column", justifyContent: "space-between",
      boxShadow: "0 8px 32px rgba(0,0,0,0.25)", transition: "transform 0.15s",
      border: "1px solid rgba(255,255,255,0.06)", minHeight: 150,
      WebkitTapHighlightColor: "transparent",
    }} data-testid={`preview-card-${label.toLowerCase()}`}>
      <div>
        <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.5)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
        {extra && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", lineHeight: 1.3 }}>{extra}</div>}
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.7)", marginTop: 3 }}>{sub}</div>
      </div>
    </div>
  );
}

// ═══ Per-card unique body renderers ══════════════════════════════════════════
// Each body shows a SOLID mini-preview of what the card is for — no see-through,
// no shared template. Double-tap opens the full expanded view.

function AccountBody({ sparkline, eq, dp, dpPct, accent, inner, breakdown }) {
  // Mini equity sparkline — SVG polyline
  let path = "";
  if (sparkline && sparkline.length > 2) {
    const vals = sparkline.map(p => p.e || p[1] || 0);
    const min = Math.min(...vals), max = Math.max(...vals);
    const range = (max - min) || 1;
    const W = 280, H = 58;
    path = vals.map((v, i) => {
      const x = (i / (vals.length - 1)) * W;
      const y = H - ((v - min) / range) * H;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(" ");
  }
  const badgeBg = dp >= 0 ? "#14532d" : "#7f1d1d";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 11, color: "#cbd5e1", letterSpacing: 0.5 }}>Last 30 bars</div>
        <div style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: badgeBg, color: dp >= 0 ? "#86efac" : "#fca5a5", fontWeight: 700 }}>
          {dp >= 0 ? "▲" : "▼"} {Math.abs(dpPct).toFixed(2)}%
        </div>
      </div>
      {path ? (
        <svg viewBox="0 0 280 58" width="100%" height="58" style={{ overflow: "visible" }}>
          <defs>
            <linearGradient id="sp" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity="0.45" />
              <stop offset="100%" stopColor={accent} stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <path d={`${path} L 280 58 L 0 58 Z`} fill="url(#sp)" />
          <path d={path} fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <div style={{ height: 58, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#94a3b8" }}>Loading equity history…</div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>
        <div style={{ padding: "8px 10px", borderRadius: 10, background: inner }}>
          <div style={{ fontSize: 9, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1 }}>Today</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: dp >= 0 ? "#4ade80" : "#f87171", marginTop: 2 }}>{dp >= 0 ? "+" : ""}{f$(dp)}</div>
        </div>
        <div style={{ padding: "8px 10px", borderRadius: 10, background: inner }}>
          <div style={{ fontSize: 9, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1 }}>Equity</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginTop: 2 }}>{f$(eq)}</div>
        </div>
      </div>
      {/* Iter 98 — per-asset-class P/L mini-breakdown. Hidden if all slices are 0. */}
      {breakdown && (breakdown.crypto?.dayPnl || breakdown.option?.dayPnl || breakdown.equity?.dayPnl) !== undefined && (() => {
        const slices = [
          { k: "equity", label: "Stock", dot: "#60a5fa" },
          { k: "option", label: "Options", dot: "#a78bfa" },
          { k: "crypto", label: "Crypto", dot: "#fbbf24" },
        ].filter(s => (breakdown[s.k]?.count || 0) > 0);
        if (slices.length === 0) return null;
        return (
          <div data-testid="account-breakdown" style={{
            display: "flex", gap: 6, paddingTop: 8,
            borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 2,
          }}>
            {slices.map(s => {
              const v = breakdown[s.k] || {};
              const pnl = v.dayPnl || 0;
              const isPos = pnl >= 0;
              return (
                <div key={s.k} style={{
                  flex: 1, padding: "6px 8px", borderRadius: 8,
                  background: "rgba(0,0,0,0.20)",
                  display: "flex", flexDirection: "column", gap: 2,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot }} />
                    <span style={{ fontSize: 9, color: "#cbd5e1", letterSpacing: 0.8, fontWeight: 700 }}>{s.label.toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: isPos ? "#86efac" : "#fca5a5", fontVariantNumeric: "tabular-nums" }}>
                    {isPos ? "+" : ""}{f$(pnl)}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

function PositionsBody({ positions, accent, inner }) {
  const top3 = positions.slice(0, 3);
  if (top3.length === 0) return (
    <div style={{ padding: 16, borderRadius: 12, background: inner, fontSize: 12, color: "#cbd5e1", textAlign: "center" }}>
      No open positions. Double-tap to explore Options Hub.
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {top3.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 10, background: inner }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>{p.ticker}</span>
            {p.type === "OPTION" && <span style={{ fontSize: 10, color: accent, marginTop: 1 }}>${p.strike}{p.side?.[0] || ""}</span>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{f$(p.currentValue)}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: p.dayGainPct >= 0 ? "#4ade80" : "#f87171" }}>
              {p.dayGainPct >= 0 ? "+" : ""}{fN(p.dayGainPct, 1)}%
            </div>
          </div>
        </div>
      ))}
      {positions.length > 3 && (
        <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center", marginTop: 4 }}>
          +{positions.length - 3} more · double-tap to see all
        </div>
      )}
    </div>
  );
}

function OptionsBody({ topPicks, accent, inner }) {
  if (!topPicks || topPicks.length === 0) return (
    <div style={{ padding: 16, borderRadius: 12, background: inner, fontSize: 12, color: "#cbd5e1", textAlign: "center" }}>
      Scanning chains… Double-tap to explore live
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 10, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Top-Zi picks</div>
      {topPicks.slice(0, 3).map((p, i) => {
        const badgeBg = p.ziScore >= 75 ? "#14532d" : p.ziScore >= 60 ? "#78350f" : "#7f1d1d";
        const badgeFg = p.ziScore >= 75 ? "#86efac" : p.ziScore >= 60 ? "#fcd34d" : "#fca5a5";
        return (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 10, background: inner }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#fff" }}>{p.ticker}</span>
              {p.strike != null && <span style={{ fontSize: 10, color: accent }}>${p.strike}{(p.optionType || "").toUpperCase().charAt(0)}</span>}
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: badgeBg, color: badgeFg }}>
              Zi {p.ziScore ?? "?"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function AutopilotBody({ apStatus, feedEvents, accent, inner }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {apStatus && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          <div style={{ padding: "8px 10px", borderRadius: 8, background: inner }}>
            <div style={{ fontSize: 9, color: "#cbd5e1", textTransform: "uppercase" }}>Mode</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginTop: 2 }}>{apStatus.mode || apStatus.autopilot_mode || "ALERT"}</div>
          </div>
          <div style={{ padding: "8px 10px", borderRadius: 8, background: inner }}>
            <div style={{ fontSize: 9, color: "#cbd5e1", textTransform: "uppercase" }}>Scanned</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: accent, marginTop: 2 }}>{apStatus.last_scan_count ?? apStatus.scanned ?? "—"} tickers</div>
          </div>
        </div>
      )}
      <div style={{ fontSize: 10, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Latest thinking</div>
      {feedEvents && feedEvents.length > 0 ? feedEvents.slice(0, 3).map((e, i) => (
        <div key={i} style={{ padding: "8px 10px", borderRadius: 8, background: inner, fontSize: 11, color: "#f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: accent, flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.message || e.text || e.event}</span>
        </div>
      )) : (
        <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", padding: 8 }}>Feed idle · waiting for market signal</div>
      )}
    </div>
  );
}

function IntelBody({ notifList, accent, inner }) {
  if (!notifList || notifList.length === 0) return (
    <div style={{ padding: 16, borderRadius: 12, background: inner, fontSize: 12, color: "#cbd5e1", textAlign: "center" }}>
      All caught up — no new alerts
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 10, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1 }}>Latest alerts</div>
      {notifList.slice(0, 3).map((n, i) => {
        const catColor = n.category === "risk" ? "#f87171" : n.category === "order" ? "#4ade80" : n.category === "intel" ? "#fbbf24" : accent;
        return (
          <div key={i} style={{ padding: "9px 11px", borderRadius: 10, background: inner, borderLeft: `3px solid ${catColor}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {n.title || n.type || "Alert"}
            </div>
            <div style={{ fontSize: 10, color: "#e2e8f0", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {n.message || ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SettingsBody({ mobileUiMode, accent, inner }) {
  const theme = (typeof document !== "undefined" && document.documentElement.getAttribute("data-theme")) || "dark";
  const modeLabel = {
    swipe: "Swipe Cards",
    swipe2: "Mobile 2.0",
    swipe3: "Trading Floor",
    glass: "UI Glass",
    advanced: "Advanced",
  }[mobileUiMode] || "Advanced";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <div style={{ padding: "10px 12px", borderRadius: 10, background: inner }}>
        <div style={{ fontSize: 9, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1 }}>UI Mode</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginTop: 3 }}>{modeLabel}</div>
      </div>
      <div style={{ padding: "10px 12px", borderRadius: 10, background: inner }}>
        <div style={{ fontSize: 9, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1 }}>Theme</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: accent, marginTop: 3, textTransform: "capitalize" }}>{theme}</div>
      </div>
      <div style={{ gridColumn: "1 / -1", padding: "10px 12px", borderRadius: 10, background: inner, fontSize: 10, color: "#cbd5e1", lineHeight: 1.5 }}>
        Push alerts · SMS · Risk gates · Sign out · All preferences in one place
      </div>
    </div>
  );
}

// ═══ TINDER-STYLE HERO CARD ══════════════════════════════════════════════════
function HeroCard({ spec, offset, onDoubleTap, onTap, zIndex, data, onPeekChange }) {
  const tapRef = useRef({ count: 0, timer: null });
  const pressRef = useRef({ timer: null, startX: 0, startY: 0, moved: false });
  const [glow, setGlow] = useState(false);
  const [peeking, setPeeking] = useState(false);
  // When this card becomes the center (offset === 0), flash a color-tinted settle glow.
  useEffect(() => {
    if (offset !== 0) return;
    setGlow(true);
    const t = setTimeout(() => setGlow(false), 540);
    return () => clearTimeout(t);
  }, [offset, spec.id]);
  // Notify parent when peek state changes so it can dim the backdrop / lock swipe
  useEffect(() => { onPeekChange?.(peeking); }, [peeking, onPeekChange]);

  // Clean up pending tap / press timers on unmount to prevent state updates on an unmounted component
  useEffect(() => {
    const tap = tapRef.current;
    const press = pressRef.current;
    return () => {
      if (tap.timer) clearTimeout(tap.timer);
      if (press.timer) clearTimeout(press.timer);
    };
  }, []);

  const enterPeek = () => {
    if (offset !== 0) return;
    hapticMedium();
    setPeeking(true);
  };
  const exitPeek = () => setPeeking(false);

  const handlePointerDown = (e) => {
    if (offset !== 0) return;
    pressRef.current.startX = e.clientX ?? 0;
    pressRef.current.startY = e.clientY ?? 0;
    pressRef.current.moved = false;
    if (pressRef.current.timer) clearTimeout(pressRef.current.timer);
    pressRef.current.timer = setTimeout(() => {
      if (!pressRef.current.moved) enterPeek();
    }, 460);
  };
  const handlePointerMove = (e) => {
    if (!pressRef.current.timer && !peeking) return;
    const dx = Math.abs((e.clientX ?? 0) - pressRef.current.startX);
    const dy = Math.abs((e.clientY ?? 0) - pressRef.current.startY);
    if (dx > 8 || dy > 8) {
      pressRef.current.moved = true;
      if (pressRef.current.timer) { clearTimeout(pressRef.current.timer); pressRef.current.timer = null; }
      if (peeking) exitPeek();
    }
  };
  const handlePointerUp = () => {
    if (pressRef.current.timer) { clearTimeout(pressRef.current.timer); pressRef.current.timer = null; }
    if (peeking) exitPeek();
  };

  const handleTap = () => {
    if (peeking) return; // during peek, ignore single-tap
    tapRef.current.count++;
    if (tapRef.current.timer) clearTimeout(tapRef.current.timer);
    tapRef.current.timer = setTimeout(() => {
      if (tapRef.current.count >= 2) { hapticMedium(); soundTap(); onDoubleTap?.(); } else { onTap?.(); }
      tapRef.current.count = 0;
    }, 250);
  };

  const abs = Math.abs(offset);
  // Apple "peek" — center card scales up when long-pressed.
  const peekScale = peeking ? 1.06 : 1;
  const scale = (offset === 0 ? peekScale : 1 - Math.min(abs * 0.05, 0.15));
  const translateX = offset === 0 ? 0 : offset * 14;
  // Peripheral cards stay fully opaque (solid, not translucent). Only distant cards off-screen fade.
  const opacity = abs > 2 ? 0 : 1;

  const renderBody = () => {
    switch (spec.body) {
      case "account":    return <AccountBody sparkline={data.sparkline} eq={data.eq} dp={data.dp} dpPct={data.dpPct} breakdown={data.breakdown} accent={spec.accent} inner={spec.inner} />;
      case "positions":  return <PositionsBody positions={data.positions} accent={spec.accent} inner={spec.inner} />;
      case "options":    return <OptionsBody topPicks={data.topPicks} accent={spec.accent} inner={spec.inner} />;
      case "autopilot":  return <AutopilotBody apStatus={data.apStatus} feedEvents={data.feedEvents} accent={spec.accent} inner={spec.inner} />;
      case "intel":      return <IntelBody notifList={data.notifList} accent={spec.accent} inner={spec.inner} />;
      case "settings":   return <SettingsBody mobileUiMode={data.mobileUiMode} accent={spec.accent} inner={spec.inner} />;
      default:           return null;
    }
  };

  return (
    <div
      data-testid={`hero-card-${spec.id}`}
      onClick={handleTap}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerUp}
      className={glow ? "zi-card-settle" : undefined}
      style={{
        position: "absolute", inset: 0,
        background: spec.bg,  // SOLID color — no transparency, no see-through
        borderRadius: 28,
        padding: 22,
        display: "flex", flexDirection: "column", gap: 16,
        boxShadow: peeking
          ? `0 36px 90px rgba(0,0,0,0.75), 0 0 0 2px ${spec.accent}66`
          : offset === 0
            ? "0 24px 60px rgba(0,0,0,0.55)"
            : "0 10px 30px rgba(0,0,0,0.35)",
        cursor: "pointer", color: "#fff",
        transform: `translateX(${translateX}px) scale(${scale})`,
        opacity, zIndex,
        transition: "transform 220ms cubic-bezier(.2,.9,.3,1), opacity 260ms, box-shadow 220ms",
        WebkitTapHighlightColor: "transparent", userSelect: "none",
        overflow: "hidden",
        // color-tinted glow ring — consumed by the .zi-card-settle keyframes
        ["--zi-glow"]: `${spec.accent}99`,
      }}
    >
      {/* Accent tag strip */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: spec.accent }} />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: spec.accent, marginBottom: 8 }}>{spec.label}</div>
          <div style={{ fontSize: 34, fontWeight: 800, color: spec.valueColor || "#fff", letterSpacing: -0.8, lineHeight: 1.05 }}>{spec.value}</div>
          {spec.sub && <div style={{ fontSize: 13, fontWeight: 600, color: spec.subColor || "#e2e8f0", marginTop: 4 }}>{spec.sub}</div>}
        </div>
      </div>

      {/* UNIQUE BODY PER CARD */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {renderBody()}
      </div>

      {/* Footer CTA */}
      <div style={{ fontSize: 10, color: "#cbd5e1", display: "flex", alignItems: "center", gap: 6, paddingTop: 4 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: spec.accent }} />
        {peeking ? "Release to close · Double-tap to pop open" : "Double-tap to open · swipe to browse"}
      </div>
    </div>
  );
}

// ═══ MAIN EXPORT ═════════════════════════════════════════════════════════════
// ═══ SWIPEABLE POSITION ROW — iOS-style swipe-left to reveal action buttons ══
function SwipeablePositionRow({ p, isLast, onOpen, onSell, onBuyMore, onTrail, onAiExit }) {
  const [dx, setDx] = useState(0);
  const startRef = useRef({ x: 0, y: 0, tracking: false, startDx: 0 });
  const ACTIONS_W = 240; // 4 action buttons × 60 wide (iter 90: added AI Exit)

  const startDrag = (clientX, clientY) => {
    startRef.current = { x: clientX, y: clientY, tracking: true, startDx: dx };
  };
  const moveDrag = (clientX, clientY) => {
    if (!startRef.current.tracking) return;
    const deltaX = clientX - startRef.current.x;
    const deltaY = clientY - startRef.current.y;
    if (Math.abs(deltaY) > Math.abs(deltaX) + 6) { startRef.current.tracking = false; return; }
    const next = Math.max(-ACTIONS_W, Math.min(0, startRef.current.startDx + deltaX));
    setDx(next);
  };
  const endDrag = () => {
    if (!startRef.current.tracking) return;
    startRef.current.tracking = false;
    // Snap open if past half the action width
    if (dx < -ACTIONS_W / 2) {
      setDx(-ACTIONS_W);
      hapticTap();
      try { localStorage.setItem("zi_swipe_learned", "1"); } catch {}
      window.dispatchEvent(new Event("zi-swipe-learned"));
    } else {
      setDx(0);
    }
  };

  // Pointer events — primary path (covers mouse, pen, and most modern touch)
  const onPointerDown = (e) => startDrag(e.clientX, e.clientY);
  const onPointerMove = (e) => moveDrag(e.clientX, e.clientY);
  const onPointerUp = endDrag;

  // Touch events — fallback for older iOS WebKit where Pointer Events are spotty
  const onTouchStart = (e) => {
    const t = e.touches[0];
    if (t) startDrag(t.clientX, t.clientY);
  };
  const onTouchMove = (e) => {
    const t = e.touches[0];
    if (t) moveDrag(t.clientX, t.clientY);
  };
  const onTouchEnd = endDrag;

  // Progress ratio for preview tile (0 → nothing revealed, 1 → fully revealed)
  const progress = Math.min(1, Math.abs(dx) / ACTIONS_W);
  const previewLabel = progress < 0.25 ? "Buy" : progress < 0.5 ? "Trail" : progress < 0.75 ? "AI Exit" : "Sell";
  const previewColor = progress < 0.25 ? "#4ade80" : progress < 0.5 ? "#fbbf24" : progress < 0.75 ? "#a78bfa" : "#f87171";

  const gainColor = (p.gainPct || 0) >= 0 ? "#4ade80" : "#f87171";
  const badge = { HOLD: { bg: "rgba(96,165,250,0.15)", fg: "#60a5fa" }, "TRAIL STOP": { bg: "rgba(74,222,128,0.15)", fg: "#4ade80" }, "SCALE OUT": { bg: "rgba(74,222,128,0.15)", fg: "#4ade80" }, "TIGHTEN RISK": { bg: "rgba(251,191,36,0.15)", fg: "#fbbf24" }, "EVALUATE EXIT": { bg: "rgba(248,113,113,0.15)", fg: "#f87171" }, EXIT: { bg: "rgba(239,68,68,0.15)", fg: "#ef4444" } }[p.action] || { bg: "rgba(96,165,250,0.15)", fg: "#60a5fa" };

  return (
    <div style={{ position: "relative", overflow: "hidden", borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.05)" }}>
      {/* Revealed action buttons behind the row */}
      <div style={{
        position: "absolute", top: 0, right: 0, bottom: 0,
        display: "flex", alignItems: "stretch", pointerEvents: dx < -20 ? "auto" : "none",
      }}>
        <button data-testid={`swipe-buy-${p.ticker}`} onClick={(e) => { e.stopPropagation(); hapticTap(); setDx(0); onBuyMore(p); }}
          style={{ width: 60, border: "none", cursor: "pointer", background: "rgba(74,222,128,0.22)", color: "#4ade80", fontSize: 10, fontWeight: 700, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 }}>
          <span style={{ fontSize: 16 }}>+</span>Buy
        </button>
        <button data-testid={`swipe-trail-${p.ticker}`} onClick={(e) => { e.stopPropagation(); hapticTap(); setDx(0); onTrail(p); }}
          style={{ width: 60, border: "none", cursor: "pointer", background: "rgba(251,191,36,0.22)", color: "#fbbf24", fontSize: 10, fontWeight: 700, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
          Trail
        </button>
        <button data-testid={`swipe-ai-exit-${p.ticker}`} onClick={(e) => { e.stopPropagation(); hapticTap(); setDx(0); onAiExit && onAiExit(p); }}
          style={{ width: 60, border: "none", cursor: "pointer", background: "rgba(167,139,250,0.22)", color: "#a78bfa", fontSize: 9, fontWeight: 700, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, lineHeight: 1.1 }}>
          <span style={{ fontSize: 14 }}>★</span>AI Exit
        </button>
        <button data-testid={`swipe-sell-${p.ticker}`} onClick={(e) => { e.stopPropagation(); hapticTap(); setDx(0); onSell(p); }}
          style={{ width: 60, border: "none", cursor: "pointer", background: "rgba(239,68,68,0.22)", color: "#f87171", fontSize: 10, fontWeight: 700, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 }}>
          <span style={{ fontSize: 16 }}>−</span>Sell
        </button>
      </div>

      {/* The row itself — translates left when swiped */}
      <div
        data-testid={`live-pos-${p.ticker}`}
        onClick={() => { if (dx === 0) onOpen(); else setDx(0); }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "12px 4px", cursor: "pointer",
          background: "var(--bg)",
          transform: `translateX(${dx}px)`,
          transition: startRef.current.tracking ? "none" : "transform 220ms cubic-bezier(.2,.9,.3,1)",
          touchAction: "pan-y",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* Preview phantom tile — shows which action will fire on release */}
        {progress > 0.12 && startRef.current.tracking && (
          <div
            data-testid={`swipe-preview-${p.ticker}`}
            style={{
              position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)",
              padding: "5px 10px", borderRadius: 10,
              background: `${previewColor}22`, border: `1px solid ${previewColor}`,
              color: previewColor, fontSize: 10, fontWeight: 700, letterSpacing: 1,
              pointerEvents: "none", opacity: Math.min(1, progress * 1.4),
            }}
          >
            → {previewLabel.toUpperCase()}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          {/* iter 97 — crypto holdings get a tiny coin-gradient dot for scannability */}
          {String(p.type || "").toUpperCase() === "CRYPTO" && (
            <span style={{
              width: 18, height: 18, borderRadius: "50%",
              background: `linear-gradient(135deg, ${COIN_GRADIENTS[p.ticker]?.[0] || "#0891b2"}, ${COIN_GRADIENTS[p.ticker]?.[1] || "#0d9488"})`,
              flexShrink: 0,
              boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
            }} />
          )}
          <span style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>{p.ticker}</span>
          {p.type === "OPTION" && <span style={{ fontSize: 12, color: "var(--blue)", fontWeight: 600 }}>${p.strike}{p.side?.[0] || ""}</span>}
          {p.action && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: badge.bg, color: badge.fg, letterSpacing: 0.5 }}>
              {p.action}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{f$(p.currentValue)}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: gainColor }}>{(p.gainPct || 0) >= 0 ? "+" : ""}{fN(p.gainPct, 1)}%</div>
          </div>
          <span style={{ color: "var(--muted)", fontSize: 18 }}>›</span>
        </div>
      </div>
    </div>
  );
}

// ═══ ELITE PICKS — AI-ranked contracts view ═══════════════════════════════════


// ═══ AI EXIT VERDICT MODAL — moved to ./modals/AiExitVerdictModal.js ═══════
// Re-export at top of file for back-compat. Kept here as comment so future
// greps don't miss it.



// ═══ EXECUTION CONFIRM MODAL — moved to ./modals/ExecutionConfirmModal.js ═══
// Re-export happens at the top of the file. This keeps SwipeCards focused on
// mobile-only card UI and makes the modal reusable from OptionsTradingFlow.


// ═══ CRYPTO EXPANDED — universe + coin deep-dive (iter 92/94) ═══════════════
// Modernized in iter 95: asymmetric layout, coin-specific gradients, market-
// pulse pill row, Buy/Sell CTAs on universe rows + detail view (wired to
// QuickTradeModal → ExecutionConfirmModal → /order/place).

// Per-coin gradient palette — each major crypto gets a recognizable duotone.
const COIN_GRADIENTS = {
  BTC: ["#f7931a", "#ffa734"],
  ETH: ["#627eea", "#8a9ef0"],
  SOL: ["#9945ff", "#14f195"],
  XRP: ["#23292f", "#5c6670"],
  ADA: ["#0033ad", "#3468d1"],
  DOGE: ["#c2a633", "#ebd55a"],
  AVAX: ["#e84142", "#ff6b6c"],
  LINK: ["#2a5ada", "#4e7df0"],
  LTC: ["#345d9d", "#5a85c9"],
  DOT: ["#e6007a", "#ff4fa0"],
  BCH: ["#0ac18e", "#40e0b2"],
  UNI: ["#ff007a", "#ff4ea0"],
  SHIB: ["#e11d48", "#f43f5e"],
  MATIC: ["#8247e5", "#a76df4"],
  NEAR: ["#00c08b", "#2dd4b0"],
  XLM: ["#14b8a6", "#2dd4bf"],
  AAVE: ["#b6509e", "#d470bf"],
  MKR: ["#1aab9b", "#3cc3b3"],
  ATOM: ["#2e3148", "#4a4d66"],
  COMP: ["#00d395", "#2de9ad"],
  FIL: ["#0090ff", "#36b0ff"],
  SAND: ["#00adef", "#36c5ff"],
  MANA: ["#ff2d55", "#ff5c7b"],
  APE: ["#0054fa", "#3479ff"],
  CRV: ["#ef4444", "#ff6b6b"],
};
const _coinGradient = (sym) => COIN_GRADIENTS[sym] || ["#0891b2", "#0d9488"];



export default function SwipeCards({ onClose, setMobileUiMode, mobileUiMode }) {
  const [expanded, setExpanded] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [watch, setWatch] = useState(null);
  const [unread, setUnread] = useState(0);
  const [notifList, setNotifList] = useState([]);
  const [apStatus, setApStatus] = useState(null);
  const [feedEvents, setFeedEvents] = useState([]);
  const [topPicks, setTopPicks] = useState([]);
  // Inline quick-trade/trail state driven by swipe-row actions
  const [quickTrade, setQuickTrade] = useState(null);
  const [trailStop, setTrailStop] = useState(null);
  const [aiExit, setAiExit] = useState(null);  // {position} → shows AiExitVerdictModal
  const [lossRecovery, setLossRecovery] = useState(null);  // {capital, lossAmount} — iter 100
  const [pnlApi, setPnlApi] = useState(null);
  const [regime, setRegime] = useState(null);  // iter 101 — market regime badge data
  useEffect(() => {
    const l = () => axios.get(`${API}/omega/market-regime`).then(r => setRegime(r.data)).catch(() => {});
    l();
    const iv = setInterval(l, 180000);  // refresh every 3 min
    return () => clearInterval(iv);
  }, []);
  // Iter 100 — poll daily P/L at top level so auto-open recovery works
  // regardless of which expanded view is active.
  useEffect(() => {
    const l = () => fastGetAsync(`${API}/daily-pnl`).then(r => setPnlApi(r.data)).catch(() => {});
    l();
    const iv = setInterval(l, 30000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    if (!pnlApi) return;
    const dpPct = pnlApi.dayPnlPct;
    const totalDayPnl = pnlApi.totalDayPnl;
    const equity = pnlApi.totalEquity;
    if (dpPct != null && dpPct <= -5 && totalDayPnl != null && equity > 0) {
      const alreadyShown = sessionStorage.getItem("zi_lossrecovery_shown_today");
      if (!alreadyShown) {
        sessionStorage.setItem("zi_lossrecovery_shown_today", "1");
        setLossRecovery({ capital: equity, lossAmount: Math.abs(totalDayPnl) });
      }
    }
  }, [pnlApi]);
  // "Hero mode" — pin the Total Equity hero at the top of the home grid
  const [heroMode, setHeroMode] = useState(() => {
    try { return localStorage.getItem("zi_hero_mode") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("zi_hero_mode", heroMode ? "1" : "0"); } catch {}
  }, [heroMode]);
  // Hide "Swipe ← for quick actions" hint once the user has swiped at least once
  const [swipeLearned, setSwipeLearned] = useState(() => {
    try { return localStorage.getItem("zi_swipe_learned") === "1"; } catch { return false; }
  });
  useEffect(() => {
    const h = () => setSwipeLearned(true);
    window.addEventListener("zi-swipe-learned", h);
    return () => window.removeEventListener("zi-swipe-learned", h);
  }, []);
  // Decrement the Intel-tile unread badge instantly when IntelExpanded marks one read.
  useEffect(() => {
    const h = () => setUnread((n) => Math.max(0, (n || 0) - 1));
    window.addEventListener("zi-notif-read", h);
    return () => window.removeEventListener("zi-notif-read", h);
  }, []);
  const [sparkline, setSparkline] = useState([]);
  // (Swipe/peek state removed — home is now a static 2-column grid.)
  const [theme, setTheme] = useState(() =>
    typeof document !== "undefined" ? (document.documentElement.getAttribute("data-theme") || "dark") : "dark"
  );
  // Live-update cardSpecs when the user flips theme in Mobile Settings
  useEffect(() => {
    if (typeof document === "undefined") return;
    const obs = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute("data-theme") || "dark");
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const [isStandalone, setIsStandalone] = useState(() =>
    mobileUiMode === "swipe" && window.matchMedia("(max-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsStandalone(mobileUiMode === "swipe" && mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update); };
  }, [mobileUiMode]);

  // Pre-load all card data on mount (so expanded views open instantly AND hero cards are rich)
  // PERF: single batched /api/preload call instead of 7 parallel HTTP round-trips.
  // Server already has a 3s in-memory cache; we keep a 20s polling rhythm.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/preload`);
        if (!alive || !r.data) return;
        const d = r.data;
        if (d.dailyPnl) setPnl(d.dailyPnl);
        if (d.openPositions) setWatch(d.openPositions);
        if (Array.isArray(d.notifications)) {
          setUnread(d.notifications.filter(n => !n.read).length);
          setNotifList(d.notifications.slice(0, 3));
        }
        if (d.omega) setApStatus(d.omega);
        if (Array.isArray(d.feedEvents)) setFeedEvents(d.feedEvents);
        if (Array.isArray(d.topPicks)) setTopPicks(d.topPicks);
        if (Array.isArray(d.equitySparkline)) setSparkline(d.equitySparkline);
      } catch {
        /* preload errored — fall back to per-endpoint loads on next tick */
      }
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // PEEK & POP — Apple-style long-press preview on home tiles.
  //   * press-and-hold 380ms → tile scales down + peek preview overlay fades in
  //   * release while still over the tile → expand fully (pop)
  //   * drag finger off OR move >8px (i.e. scrolling) → cancel cleanly
  const [peekId, setPeekId] = useState(null);        // which tile is peeking
  const [peekReady, setPeekReady] = useState(false); // true once 380ms elapsed
  const peekTimerRef = useRef(null);
  const peekStartPointRef = useRef(null);
  const MOVE_CANCEL_PX = 8; // scroll-intent threshold
  const beginPeek = (id, clientX, clientY) => {
    peekStartPointRef.current = { x: clientX, y: clientY };
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    peekTimerRef.current = setTimeout(() => {
      // Only arm if pointer hasn't moved significantly (scroll detection)
      setPeekId(id);
      setPeekReady(true);
      try { hapticMedium(); } catch {}
    }, 380);
  };
  const onPeekMove = (e) => {
    const start = peekStartPointRef.current;
    if (!start) return;
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (dx > MOVE_CANCEL_PX || dy > MOVE_CANCEL_PX) {
      cancelPeek();
    }
  };
  const cancelPeek = () => {
    if (peekTimerRef.current) { clearTimeout(peekTimerRef.current); peekTimerRef.current = null; }
    peekStartPointRef.current = null;
    setPeekId(null);
    setPeekReady(false);
  };
  const releasePeek = (id) => {
    if (peekReady && peekId === id) {
      // POP — open card
      setPeekId(null);
      setPeekReady(false);
      hapticSuccess && hapticSuccess();
      setExpanded(id);
    } else {
      cancelPeek();
    }
  };
  useEffect(() => () => { if (peekTimerRef.current) clearTimeout(peekTimerRef.current); }, []);

  const eq = pnl?.totalEquity || 0;
  const dp = pnl?.totalDayPnl || 0;
  const dpPct = pnl?.dayPnlPct || 0;
  const positions = watch?.positions || [];
  const pc = watch?.count || positions.length || 0;

  // Hero specs with SOLID backgrounds + matching SOLID inner-surface shade.
  // Inner pills (`inner`) are a lighter solid tint of each card's bg — zero alpha.
  // Light theme uses slightly brighter mid-tone solids so the white text still pops.
  const isLight = theme === "light";
  const cardSpecs = [
    { id: "overview", label: "Account",
      bg: isLight ? "linear-gradient(140deg, #1e40af, #2563eb)" : "linear-gradient(140deg, #0a2540, #1e3a8a)",
      accent: isLight ? "#93c5fd" : "#38bdf8",
      preSub: `${dpPct >= 0 ? "+" : ""}${fN(dpPct, 1)}%`,
      value: f$(eq), valueColor: "#fff",
      sub: `${dp >= 0 ? "+" : ""}${f$(dp)} today` },
    { id: "positions", label: "Positions",
      bg: isLight ? "linear-gradient(140deg, #15803d, #16a34a)" : "linear-gradient(140deg, #064e3b, #15803d)",
      accent: isLight ? "#bbf7d0" : "#34d399",
      preSub: "Live P&L + Greeks",
      value: `${pc}`, valueColor: "#fff",
      sub: pc === 1 ? "Open trade" : "Open trades" },
    { id: "options", label: "Options",
      bg: isLight ? "linear-gradient(140deg, #6d28d9, #7c3aed)" : "linear-gradient(140deg, #3b0764, #6d28d9)",
      accent: isLight ? "#e9d5ff" : "#c4b5fd",
      preSub: "Chart · Chain · Execute",
      value: "Trade", valueColor: "#fff",
      sub: "Discovery" },
    { id: "autopilot", label: "Autopilot",
      bg: isLight ? "linear-gradient(140deg, #b91c1c, #dc2626)" : "linear-gradient(140deg, #7f1d1d, #b91c1c)",
      accent: isLight ? "#fecaca" : "#fca5a5",
      preSub: "Live thinking",
      value: "AI", valueColor: "#fff",
      sub: "Kill switches · Feed" },
    { id: "intel", label: "Intel",
      bg: isLight ? "linear-gradient(140deg, #c2410c, #ea580c)" : "linear-gradient(140deg, #7c2d12, #c2410c)",
      accent: isLight ? "#fed7aa" : "#fdba74",
      preSub: "Notifications",
      value: `${unread}`, valueColor: "#fff",
      sub: unread === 1 ? "Unread alert" : "Unread alerts",
      badge: unread > 0 ? unread : null },
    { id: "brain", label: "Omega Brain",
      bg: isLight ? "linear-gradient(140deg, #6d28d9, #4c1d95)" : "linear-gradient(140deg, #1e1b4b, #4c1d95)",
      accent: isLight ? "#ddd6fe" : "#c4b5fd",
      preSub: "All 5 engines · live",
      value: "Ω", valueColor: "#fff",
      sub: "Status · toggles" },
    { id: "tea", label: "Omega Tea",
      bg: isLight ? "linear-gradient(140deg, #be185d, #9d174d)" : "linear-gradient(140deg, #500724, #831843)",
      accent: isLight ? "#fbcfe8" : "#f9a8d4",
      preSub: "AI news feed · cross-sourced",
      value: "🍵", valueColor: "#fff",
      sub: "Yahoo · Finnhub · Whale · Hedge fund" },
    { id: "settings", label: "Settings",
      bg: isLight ? "linear-gradient(140deg, #475569, #64748b)" : "linear-gradient(140deg, #1e293b, #334155)",
      accent: isLight ? "#e2e8f0" : "#cbd5e1",
      preSub: "Account · Sign out",
      value: "⚙", valueColor: "#e5e7eb",
      sub: "UI mode · Theme" },
    { id: "elite", label: "Elite Picks",
      bg: isLight ? "linear-gradient(140deg, #a16207, #ca8a04)" : "linear-gradient(140deg, #713f12, #a16207)",
      accent: isLight ? "#fef08a" : "#fde047",
      preSub: "AI-ranked contracts",
      value: "★", valueColor: "#fef08a",
      sub: "Entry · Exit · Universe" },
    { id: "crypto", label: "Crypto",
      bg: isLight ? "linear-gradient(140deg, #0891b2, #0d9488)" : "linear-gradient(140deg, #083344, #155e75)",
      accent: isLight ? "#cffafe" : "#67e8f9",
      preSub: "BTC · ETH · SOL",
      value: "◎", valueColor: "#67e8f9",
      sub: "Live · Zi-ranked" },
    { id: "swing", label: "Tomorrow's Plays",
      bg: isLight ? "linear-gradient(140deg, #7c3aed, #a78bfa)" : "linear-gradient(140deg, #4c1d95, #6d28d9)",
      accent: isLight ? "#ede9fe" : "#c4b5fd",
      preSub: "Next-day swing analyzer",
      value: "↗", valueColor: "#c4b5fd",
      sub: "Entry · Stop · Target" },
    { id: "ziChat", label: "ZI Chat",
      bg: isLight ? "linear-gradient(140deg, #0ea5e9, #a78bfa)" : "linear-gradient(140deg, #0c4a6e, #4c1d95)",
      accent: isLight ? "#e0f2fe" : "#bae6fd",
      preSub: "Ask Omega anything",
      value: "AI", valueColor: "#fff",
      sub: "Claude Sonnet 4.6 · Live" },
  ];

  // Position-row action badge colors
  const actionBadge = { "HOLD": { bg: "rgba(96,165,250,0.15)", fg: "#60a5fa" }, "TRAIL STOP": { bg: "rgba(74,222,128,0.15)", fg: "#4ade80" }, "SCALE OUT": { bg: "rgba(74,222,128,0.15)", fg: "#4ade80" }, "TIGHTEN RISK": { bg: "rgba(251,191,36,0.15)", fg: "#fbbf24" }, "EVALUATE EXIT": { bg: "rgba(248,113,113,0.15)", fg: "#f87171" }, "EXIT": { bg: "rgba(239,68,68,0.15)", fg: "#ef4444" } };

  const openCard = (id) => {
    hapticTap();
    soundTap();
    setExpanded(id);
  };

  if (expanded === "options") {
    return (
      <ErrorBoundary label="Options view crashed">
        <OptionsTradingFlow onClose={() => setExpanded(null)} />
      </ErrorBoundary>
    );
  }

  if (expanded) return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "var(--bg)", overflowY: "auto", WebkitOverflowScrolling: "touch" }} data-testid="card-expanded">
      {/* STICKY header so back/close are always reachable */}
      <div style={{
        position: "sticky", top: 0, zIndex: 20,
        background: "#050505",
        boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
        paddingTop: "env(safe-area-inset-top)",
      }}>
        {!isStandalone && <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 2px" }}><div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.12)" }} /></div>}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: isStandalone ? "14px 16px 12px" : "6px 16px 12px" }}>
          <button onClick={() => { hapticTap(); setExpanded(null); }} data-testid="card-back" aria-label="Back" style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 44, height: 44, borderRadius: 22,
            background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)",
            color: "#ffffff", fontSize: 22, fontWeight: 700, cursor: "pointer", padding: 0, lineHeight: 1,
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation",
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          {!isStandalone && <button onClick={onClose} style={{ width: 38, height: 38, borderRadius: 19, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: "var(--text)", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", touchAction: "manipulation" }} data-testid="card-close">&times;</button>}
        </div>
      </div>
      {expanded === "overview" && <AccountExpanded />}
      {expanded === "positions" && <PositionsExpanded />}
      {expanded === "autopilot" && <AutopilotExpanded />}
      {expanded === "intel" && <IntelExpanded />}
      {expanded === "elite" && <EliteExpanded />}
      {expanded === "crypto" && <CryptoExpanded />}
      {expanded === "swing" && <SwingPlaysExpanded />}
      {expanded === "brain" && (
        <div style={{ padding: "12px 14px 24px", maxWidth: 720, margin: "0 auto" }} data-testid="brain-expanded">
          <div style={{ fontSize: 11, color: "var(--dim)", letterSpacing: 2, fontWeight: 700, marginBottom: 12 }}>OMEGA BRAIN</div>
          <OmegaBrainStatusCard compact={true} />
        </div>
      )}
      {expanded === "ziChat" && (
        <ErrorBoundary label="Chat is having trouble loading">
          <ZiChatExpanded portfolioContext={{ equity: eq, dayPnl: dp, dayPnlPct: dpPct, openPositions: pc }} regime={regime} />
        </ErrorBoundary>
      )}
      {expanded === "tea" && (
        <ErrorBoundary label="Omega Tea is brewing">
          <div data-testid="swipe-tea-expanded" style={{ position: "fixed", inset: 0, zIndex: 100, background: "var(--bg, #0a0a14)", overflowY: "auto" }}>
            <div style={{ position: "sticky", top: 0, padding: "calc(10px + env(safe-area-inset-top)) 16px 10px", background: "rgba(10,10,20,0.88)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 8, zIndex: 10 }}>
              <button onClick={() => setExpanded(null)} data-testid="tea-back" style={{ background: "none", border: "none", color: "var(--text)", fontSize: 22, cursor: "pointer", padding: "4px 8px" }}>‹</button>
              <span style={{ fontSize: 14, fontWeight: 800 }}>Back</span>
            </div>
            <OmegaTeaPage />
          </div>
        </ErrorBoundary>
      )}
      {expanded === "settings" && <MobileSettingsExpanded onSwitchToAdvanced={() => { if (setMobileUiMode) setMobileUiMode("advanced"); onClose(); }} onSetMobileUiMode={setMobileUiMode} currentMode={mobileUiMode} heroMode={heroMode} setHeroMode={setHeroMode} onOpenLossRecovery={() => {
        // Manual Loss Recovery trigger from Settings — synthesize params from
        // current P/L if in the red, else use a $50 hypothetical loss on
        // current equity so the planner still gives educational output.
        const eq = pnlApi?.totalEquity || 1000;
        const todaysLoss = Math.max(50, Math.abs(pnlApi?.totalDayPnl || 0));
        setLossRecovery({ capital: eq, lossAmount: todaysLoss });
      }} />}
      {/* Iter 100 — Loss recovery modal (rendered in expanded view too) */}
      {lossRecovery && (
        <LossRecoveryModal
          capital={lossRecovery.capital}
          lossAmount={lossRecovery.lossAmount}
          onClose={() => setLossRecovery(null)}
        />
      )}
    </div>
  );

  return (
    <div
      data-testid="swipe-cards-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "var(--bg)",
        display: "flex", flexDirection: "column",
        overflowY: "auto", WebkitOverflowScrolling: "touch",
        paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {!isStandalone && <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 2px" }}><div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.12)" }} /></div>}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: isStandalone ? "20px 22px 10px" : "6px 22px 10px" }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--text)", letterSpacing: -0.4 }}>Omega ZI AI</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>Tap a card to explore</div>
          <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
            <LastLoginBadge compact />
            <AutomationHealthPill compact />
          </div>
        </div>
        {!isStandalone && <button onClick={onClose} style={{ width: 38, height: 38, borderRadius: 19, background: "rgba(255,255,255,0.06)", border: "none", color: "var(--text)", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="close-swipe-cards">&times;</button>}
      </div>

      {/* HERO STRIP — pinned Total Equity if hero mode ON */}
      {heroMode && (
        <button
          data-testid="hero-strip"
          onClick={() => openCard("overview")}
          style={{
            margin: "6px 18px 4px", padding: "16px 18px",
            borderRadius: 20, border: "none", cursor: "pointer", textAlign: "left",
            background: "linear-gradient(145deg, #0a2540 0%, #1e3a8a 100%)",
            color: "#fff",
            boxShadow: "0 12px 30px rgba(10,37,64,0.45)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#38bdf8", boxShadow: "0 0 8px #38bdf8" }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: "#93c5fd", letterSpacing: 2, textTransform: "uppercase" }}>Total Equity · live</span>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: dp >= 0 ? "#14532d" : "#7f1d1d", color: dp >= 0 ? "#86efac" : "#fca5a5" }}>
              {dp >= 0 ? "▲" : "▼"} {dpPct >= 0 ? "+" : ""}{fN(dpPct, 2)}%
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, marginTop: 6 }}>
            <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: -0.8, lineHeight: 1 }}>{f$(eq)}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: dp >= 0 ? "#86efac" : "#fca5a5", paddingBottom: 3 }}>
              {dp >= 0 ? "+" : ""}{f$(dp)} today
            </div>
          </div>
        </button>
      )}

      {/* Active Orders (iter 97) — pending/queued orders with swipe-to-cancel.
          Iter 101 — Moved ABOVE the card grid so LIVE POSITIONS stays at the
          visual bottom of the mobile home without competition for attention. */}
      <ActiveOrdersStrip />

      {/* Card grid — 2 columns */}
      <div style={{ padding: "8px 18px 10px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {cardSpecs.map((spec) => (
          <button
            key={spec.id}
            data-testid={`tile-${spec.id}`}
            onClick={() => { if (peekReady) return; openCard(spec.id); }}
            onPointerDown={(e) => beginPeek(spec.id, e.clientX, e.clientY)}
            onPointerMove={onPeekMove}
            onPointerUp={() => releasePeek(spec.id)}
            onPointerLeave={cancelPeek}
            onPointerCancel={cancelPeek}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              position: "relative",
              background: spec.bg,
              borderRadius: 22, padding: "16px 16px 18px",
              minHeight: 175, border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", justifyContent: "space-between",
              textAlign: "left", color: "#fff",
              boxShadow: peekId === spec.id ? "0 18px 44px rgba(0,0,0,0.55)" : "0 10px 30px rgba(0,0,0,0.35)",
              WebkitTapHighlightColor: "transparent",
              transform: peekId === spec.id && peekReady ? "scale(0.96)" : (peekId === spec.id ? "scale(0.98)" : ""),
              transition: "transform 220ms cubic-bezier(.2,.9,.3,1.2), box-shadow 220ms ease",
              userSelect: "none",
              touchAction: "manipulation",
            }}
          >
            {spec.badge != null && (
              <span
                data-testid={`tile-${spec.id}-badge`}
                style={{
                  position: "absolute", top: 10, right: 10,
                  minWidth: 22, height: 22, padding: "0 7px",
                  borderRadius: 11,
                  background: "#ef4444", color: "#fff",
                  fontSize: 11, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 0 0 3px rgba(239,68,68,0.25), 0 4px 12px rgba(239,68,68,0.4)",
                  animation: "pulse 2s infinite",
                }}
              >{spec.badge > 99 ? "99+" : spec.badge}</span>
            )}
            {/* iter 103 — Regime badge centered in the tile's middle gap
                (between top label area and bottom value area) so it doesn't
                overlap any other tile element. */}
            {regime?.ok && (spec.id === "overview" || spec.id === "elite") && !spec.badge && (
              <span
                data-testid={`tile-${spec.id}-regime`}
                style={{
                  position: "absolute",
                  top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  padding: "4px 10px",
                  borderRadius: 8,
                  background: _regimeColorTile(regime.regime) + "22",
                  border: "1px solid " + _regimeColorTile(regime.regime) + "55",
                  color: _regimeColorTile(regime.regime),
                  fontSize: 9.5, fontWeight: 800, letterSpacing: 1.3,
                  backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
                  display: "inline-flex", alignItems: "center", gap: 5,
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                  pointerEvents: "none",
                }}
                title={regime.note}
              >
                <span style={{ fontSize: 9 }}>{_regimeDot(regime.regime)}</span>
                {regime.regime.replace("_", " ")}
              </span>
            )}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.8)" }}>{spec.label}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)", marginTop: 3, lineHeight: 1.35 }}>{spec.preSub}</div>
            </div>
            <div>
              <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.05, letterSpacing: -0.8, color: spec.valueColor || "#fff" }}>{spec.value}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.78)", marginTop: 4 }}>{spec.sub}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Iter 102 — LIVE POSITIONS bottom section removed per user request.
          Positions are still accessible via the Positions tile in the grid. */}

      {/* Quick-trade + trail-stop modals wired to swipe-row actions */}
      {quickTrade && <QuickTradeModal position={quickTrade.position} mode={quickTrade.mode} onClose={() => setQuickTrade(null)} onSuccess={() => setQuickTrade(null)} />}
      {trailStop && <TrailStopModal position={trailStop.position} onClose={() => setTrailStop(null)} onSuccess={() => setTrailStop(null)} />}
      {aiExit && <AiExitVerdictModal position={aiExit.position} onClose={() => setAiExit(null)} />}

      {/* PEEK overlay — Apple-style hover preview while user is long-pressing a tile */}
      {peekReady && peekId && (() => {
        const peekSpec = cardSpecs.find(s => s.id === peekId);
        if (!peekSpec) return null;
        const previewLines = {
          overview: [`Equity · ${f$(eq)}`, `Today · ${dp >= 0 ? "+" : ""}${f$(dp)} (${dpPct >= 0 ? "+" : ""}${fN(dpPct, 2)}%)`, `${pc} open position${pc === 1 ? "" : "s"}`],
          positions: [`${pc} open position${pc === 1 ? "" : "s"}`, positions[0] ? `Top · ${positions[0].ticker || positions[0].symbol}` : "No holdings yet", "Swipe-left on a row for actions"],
          options: ["Chart · Chain · Execute", "Build spreads live", "Find high-EV strikes"],
          autopilot: [apStatus?.active ? "Autopilot · ENGAGED" : "Autopilot · idle", `Regime · ${(apStatus?.regime || "—").replace("_", " ")}`, `${(topPicks || []).length} live top picks`],
          intel: [`${unread} unread alert${unread === 1 ? "" : "s"}`, (notifList[0]?.title || "Feed quiet — tap Refresh"), "Long-press a row to dismiss"],
          crypto: ["Live BTC / ETH / SOL prices", "Zi-ranked by momentum + volume", "Tap a coin → Claude deep-dive"],
          settings: ["Theme · UI mode", "Push notifications", "Account · sign out"],
        }[peekId] || [peekSpec.sub];
        return (
          <div
            data-testid={`peek-${peekId}`}
            style={{
              position: "fixed", inset: 0, zIndex: 20000,
              background: "rgba(0,0,0,0.55)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 24, pointerEvents: "none",
              animation: "fadeIn 180ms ease",
            }}
          >
            <div style={{
              width: "min(320px, 90vw)", borderRadius: 26, padding: "22px 22px 24px",
              background: peekSpec.bg, color: "#fff",
              boxShadow: "0 30px 80px rgba(0,0,0,0.65)",
              transform: "scale(1)", transition: "transform 200ms cubic-bezier(.2,.9,.3,1.2)",
            }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2.2, textTransform: "uppercase", color: "rgba(255,255,255,0.75)" }}>{peekSpec.label} · Peek</div>
              <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: -0.8, marginTop: 6, lineHeight: 1.05 }}>{peekSpec.value}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.82)", marginTop: 4 }}>{peekSpec.sub}</div>
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.14)" }}>
                {previewLines.map((l, i) => (
                  <div key={i} style={{ fontSize: 12, color: "rgba(255,255,255,0.88)", padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(255,255,255,0.6)" }} />
                    {l}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", marginTop: 14, textAlign: "center", letterSpacing: 1 }}>
                RELEASE TO OPEN · SLIDE AWAY TO CANCEL
              </div>
            </div>
          </div>
        );
      })()}

      {/* Iter 100 — Loss recovery modal (auto-pops on -5% day, or via Settings → Recover from loss). */}
      {lossRecovery && (
        <LossRecoveryModal
          capital={lossRecovery.capital}
          lossAmount={lossRecovery.lossAmount}
          onClose={() => setLossRecovery(null)}
        />
      )}
    </div>
  );
}
